import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  PlayCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import { smsService } from '../../services/smsService';
import { Campaign } from '../../types/campaign';
import { LeadStatus } from '../../types/lead';
import { SmsCampaignResultLead, SmsCampaignStats } from '../../types/sms';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import Button from '../../components/UI/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/UI/Card';
import LeadDetailSidebar from '../../components/Leads/LeadDetailSidebar';
import SmsLeadResultCard from '../../components/Results/SmsLeadResultCard';

type PipelineTab = 'all' | 'interested' | 'follow_up' | 'app_out' | 'app_in' | 'not_interested';

const PIPELINE_STATUSES: LeadStatus[] = ['interested', 'follow_up', 'app_out', 'app_in', 'not_interested'];
const PAGE_SIZE = 25;

interface SmsCampaignResultsViewProps {
  campaignId: string;
  campaign: Campaign;
}

const SmsCampaignResultsView: React.FC<SmsCampaignResultsViewProps> = ({ campaignId, campaign }) => {

  const [smsStats, setSmsStats] = useState<SmsCampaignStats | null>(null);
  const [leads, setLeads] = useState<SmsCampaignResultLead[]>([]);
  const [tabCountMap, setTabCountMap] = useState<Record<string, number>>({});
  const [statusFilter, setStatusFilter] = useState<PipelineTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<'today' | 'yesterday' | 'all_time'>('all_time');
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const statusList = useMemo(
    () => (statusFilter === 'all' ? undefined : [statusFilter as LeadStatus]),
    [statusFilter]
  );

  const fetchSmsStats = useCallback(async () => {
    try {
      const stats = await smsService.getCampaignSmsStats(campaignId);
      setSmsStats(stats);
    } catch {
      /* non-fatal */
    }
  }, [campaignId]);

  const fetchTabCounts = useCallback(async () => {
    try {
      const counts = await leadService.getLeadResultCounts({
        campaignId,
        search: debouncedSearch || undefined,
        dateFrom,
        dateTo,
      });
      setTabCountMap(counts.statusCounts || {});
    } catch {
      /* non-fatal */
    }
  }, [campaignId, debouncedSearch, dateFrom, dateTo]);

  const fetchLeads = useCallback(
    async (page = 1, opts?: { silent?: boolean }) => {
      try {
        if (!opts?.silent) setLoading(true);
        const data = await smsService.getCampaignSmsResults(campaignId, {
          page,
          limit: PAGE_SIZE,
          ...(statusList?.length ? { status: statusList } : {}),
          search: debouncedSearch || undefined,
          dateFrom,
          dateTo,
          sortBy: 'lastOutboundAt',
          sortOrder: 'desc',
        });
        setLeads(data.leads);
        setTotalCount(data.pagination.total);
        setTotalPages(Math.max(data.pagination.totalPages, 1));
        setCurrentPage(data.pagination.page);
      } catch {
        toast.error('Failed to load SMS results');
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [campaignId, statusList, debouncedSearch, dateFrom, dateTo]
  );

  useEffect(() => {
    void fetchSmsStats();
  }, [fetchSmsStats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    void fetchTabCounts();
  }, [fetchTabCounts]);

  useEffect(() => {
    void fetchLeads(currentPage);
  }, [fetchLeads, currentPage]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSmsStats(), fetchTabCounts(), fetchLeads(currentPage, { silent: true })]);
    setRefreshing(false);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLeadUpdate = async (
    lead: SmsCampaignResultLead,
    status: LeadStatus,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.stopPropagation();
    setUpdatingLeadId(lead.id);
    try {
      await leadService.updateLead(lead.id, { status });
      if (PIPELINE_STATUSES.includes(status)) {
        setStatusFilter(status as PipelineTab);
        setCurrentPage(1);
        await fetchLeads(1, { silent: true });
      } else {
        await fetchLeads(currentPage, { silent: true });
      }
      await fetchTabCounts();
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  const applyQuickFilter = (key: 'today' | 'yesterday' | 'all_time') => {
    setActiveQuickFilter(key);
    const now = new Date();
    if (key === 'all_time') {
      setDateFrom(undefined);
      setDateTo(undefined);
      return;
    }
    if (key === 'today') {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      setDateFrom(start.toISOString());
      setDateTo(now.toISOString());
      return;
    }
    const start = new Date(now);
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);
    setDateFrom(start.toISOString());
    setDateTo(end.toISOString());
  };

  const interestedCount = tabCountMap.interested ?? 0;
  const followUpCount = tabCountMap.follow_up ?? 0;
  const notInterestedCount = tabCountMap.not_interested ?? 0;
  const appOutCount = tabCountMap.app_out ?? 0;
  const appInCount = tabCountMap.app_in ?? 0;
  const allCount = Object.values(tabCountMap).reduce((sum, n) => sum + n, 0);

  const pipelineTabs: Array<{
    id: PipelineTab;
    label: string;
    count: number;
    icon: React.ComponentType<{ className?: string }>;
    activeColor: string;
  }> = [
    { id: 'all', label: 'All Leads', count: allCount, icon: FunnelIcon, activeColor: 'bg-primary text-primary-foreground' },
    { id: 'interested', label: 'Interested', count: interestedCount, icon: CheckCircleIcon, activeColor: 'bg-green-600 text-white dark:bg-green-500' },
    { id: 'follow_up', label: 'Follow Up', count: followUpCount, icon: PlayCircleIcon, activeColor: 'bg-blue-600 text-white dark:bg-blue-500' },
    { id: 'app_out', label: 'App Out', count: appOutCount, icon: ClockIcon, activeColor: 'bg-indigo-600 text-white dark:bg-indigo-500' },
    { id: 'app_in', label: 'App In', count: appInCount, icon: ClockIcon, activeColor: 'bg-sky-600 text-white dark:bg-sky-500' },
    { id: 'not_interested', label: 'Not Interested', count: notInterestedCount, icon: CheckCircleIcon, activeColor: 'bg-muted-foreground text-white' },
  ];

  const replyRate =
    (smsStats?.totalSent || 0) > 0
      ? Math.round(((smsStats?.inboundReplies || 0) / (smsStats?.totalSent || 1)) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign.name || 'Campaign', href: `/campaigns/${campaignId}` },
          { label: 'Results', current: true },
        ]}
      />

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-7 w-7 text-primary" />
            {campaign.name} — SMS Results
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pipeline outcomes and message delivery per lead. Use Inbox to reply.
          </p>
        </div>
        <Button variant="outlined" size="sm" onClick={() => void handleRefresh()} loading={refreshing}>
          <ArrowPathIcon className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Sent', value: (smsStats?.totalSent ?? 0).toLocaleString() },
          { label: 'Delivered', value: (smsStats?.delivered ?? 0).toLocaleString() },
          { label: 'Failed', value: (smsStats?.failed ?? 0).toLocaleString() },
          { label: 'Pending', value: (smsStats?.pending ?? 0).toLocaleString() },
          { label: 'Inbound replies', value: (smsStats?.inboundReplies ?? 0).toLocaleString() },
          { label: 'Reply rate', value: `${replyRate}%` },
          { label: 'Open threads', value: (smsStats?.openThreads ?? 0).toLocaleString() },
          { label: 'DNC / Opt-out', value: (smsStats?.dncBlocks ?? 0).toLocaleString() },
        ].map((item) => (
          <div key={item.label} className="rounded-xl border border-border bg-card px-4 py-3">
            <p className="text-2xl font-bold text-foreground">{item.value}</p>
            <p className="mt-1 text-xs font-medium text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px] max-w-md">
            <label htmlFor="sms-results-search" className="block text-xs font-medium text-muted-foreground mb-1">
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id="sms-results-search"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name, phone, company, email..."
                className="input w-full pl-9 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-lg border border-border overflow-hidden w-fit">
          {(['today', 'yesterday', 'all_time'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => applyQuickFilter(f)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                activeQuickFilter === f ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted/40'
              } ${f !== 'today' ? 'border-l border-border' : ''}`}
            >
              {f === 'today' ? 'Today' : f === 'yesterday' ? 'Yesterday' : 'All Time'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 bg-muted rounded-lg p-1">
        {pipelineTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setStatusFilter(tab.id);
                setCurrentPage(1);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                isActive ? tab.activeColor + ' shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-muted-foreground/10'}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Leads
              <span className="ml-2 text-base font-normal text-muted-foreground">({totalCount.toLocaleString()})</span>
            </CardTitle>
            {totalPages > 1 && (
              <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <FunnelIcon className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground font-medium">No leads found</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {leads.map((lead) => (
                  <SmsLeadResultCard
                    key={lead.id}
                    lead={lead}
                    updating={updatingLeadId === lead.id}
                    onStatusChange={(status, e) => void handleLeadUpdate(lead, status, e)}
                    onViewDetails={() => setSelectedLeadId(lead.id)}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
                    {totalCount.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <ChevronLeftIcon className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <LeadDetailSidebar
        leadId={selectedLeadId}
        enableContactActions={false}
        onClose={() => setSelectedLeadId(null)}
        onLeadUpdated={() => {
          void fetchLeads(currentPage, { silent: true });
          void fetchTabCounts();
        }}
      />
    </div>
  );
};

export default SmsCampaignResultsView;
